import { InputText } from 'primereact/inputtext';
import React, { useContext, useEffect, useState } from 'react';
import { CompanyFolderInDTO, CompanyFolderOutDTO, EntityTypeEnum, FolderVisibilityEnum, NotifyTargetOutDTO, NotifyTargetTypeEnum } from '@api/ringil-api.v2';

import * as yup from 'yup';
import { findValidationMessage } from '@utils/validation';
import { ApiContext } from '@api/api';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar.tsx';
import { ColWithGap } from '@components/styles.tsx';
import { VerticalFieldAndValue } from '@components/ringil3/Containers/VerticalFieldAndValue.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import useForm from '@hooks/useForm/useForm.tsx';
import InputEntityType from '@components/obsolete/Form/InputEntityType/InputEntityType.tsx';
import RingilInputSwitch from '@components/framework/input/RingilInputSwitch/RingilInputSwitch.tsx';
import NotifyDropdown from '@components/RingilComponents/NotifyDropdown.tsx';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import { Callbacks } from '@api/apiRuntime';

interface FolderForm {
  name: string;
  entityType: EntityTypeEnum;
  externalId?: string;
  restricted: boolean;
  targets: NotifyTargetOutDTO[];
}

interface ComponentProps {
  visible: boolean;
  onHide: (saved: boolean) => void;
  /** Folder being edited; undefined = create */
  folder?: CompanyFolderOutDTO;
  /** admin = Ringil admin editing any company (companyId required); customer = current company */
  mode: 'admin' | 'customer';
  companyId?: string;
  /** RD-2977: entity types the company may create folders for (from its modules) */
  entityTypes?: EntityTypeEnum[];
}

/**
 * RD-2977: create / edit a company folder incl. its visibility (everyone, or selected departments + users)
 * and the external id used by the public API. Shared by the admin company detail and the customer's
 * Company profile → Folders tab.
 */
export const EditCompanyFolderSideBar: React.FC<ComponentProps> = ({ visible, onHide, folder, mode, companyId, entityTypes }) => {
  const {
    createCompanyFolder,
    updateCompanyFolder,
    listCompanyFolderTargets,
    createCompanyFolderCustomer,
    updateCompanyFolderCustomer,
    listCompanyFolderTargetsCustomer,
  } = useContext(ApiContext);
  const { tr } = useTranslationLgs();
  const [targetOptions, setTargetOptions] = useState<NotifyTargetOutDTO[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const validationScheme = yup.object().shape({
    name: yup.string().defined().required(),
    targets: yup.array().when('restricted', {
      is: true,
      then: schema => schema.min(1, tr('EditCompanyFolderSidebar.targetsRequired', 'Select at least one department or user')),
      otherwise: schema => schema.nullable(),
    }),
  });

  const form = useForm<FolderForm>(validationScheme, null, d => save(d), false, false);

  useEffect(() => {
    if (!visible) return;
    setError(null);
    const onTargets = (data: NotifyTargetOutDTO[]) => setTargetOptions(data);
    if (mode === 'admin') listCompanyFolderTargets(companyId, onTargets);
    else listCompanyFolderTargetsCustomer(onTargets);
  }, [visible, mode, companyId]);

  useEffect(() => {
    if (!visible) return;
    form.clear();
    const selected: NotifyTargetOutDTO[] = [
      ...(folder?.departments ?? []).map(d => ({ id: d.id, name: d.name, notifyTargetType: NotifyTargetTypeEnum.DEPARTMENT })),
      ...(folder?.users ?? []).map(u => ({ id: u.id, name: u.name, notifyTargetType: NotifyTargetTypeEnum.USER })),
    ];
    form.setValues({
      name: folder?.folderName ?? '',
      entityType: folder?.entityType ?? entityTypes?.[0] ?? EntityTypeEnum.EXPEDITION,
      externalId: folder?.externalId ?? '',
      restricted: folder?.folderVisibility === FolderVisibilityEnum.RESTRICTED,
      targets: selected,
    });
  }, [visible, folder]);

  const callbacks: Callbacks = {
    onValidationFailed: (response: any) => {
      const first = Array.isArray(response) ? response[0] : null;
      setError(first?.message ?? tr('EditCompanyFolderSidebar.saveFailed', 'Folder could not be saved'));
      setSaving(false);
    },
    onError: (_, showStandardError) => {
      showStandardError();
      setSaving(false);
    },
  };

  const save = (data: FolderForm) => {
    const body: CompanyFolderInDTO = {
      name: data.name.trim(),
      entityType: data.entityType,
      externalId: data.externalId?.trim() || undefined,
      folderVisibility: data.restricted ? FolderVisibilityEnum.RESTRICTED : FolderVisibilityEnum.ALL,
      departmentIds: data.restricted ? data.targets.filter(t => t.notifyTargetType === NotifyTargetTypeEnum.DEPARTMENT).map(t => t.id) : [],
      userIds: data.restricted ? data.targets.filter(t => t.notifyTargetType === NotifyTargetTypeEnum.USER).map(t => t.id) : [],
    };
    setError(null);
    setSaving(true);
    const done = () => {
      setSaving(false);
      onHide(true);
    };
    if (mode === 'admin') {
      if (folder) updateCompanyFolder(companyId, folder.folderId, body, done, null, callbacks);
      else createCompanyFolder(companyId, body, done, null, callbacks);
    } else {
      if (folder) updateCompanyFolderCustomer(folder.folderId, body, done, null, callbacks);
      else createCompanyFolderCustomer(body, done, null, callbacks);
    }
  };

  // The visibility targets only exist for expedition folders (supplier-notification folders stay unrestricted).
  const selectedTargets = targetOptions.filter(o => form.values?.targets?.some(t => t.id === o.id && t.notifyTargetType === o.notifyTargetType));

  return (
    <RingilSidebar
      visible={visible}
      onBack={() => onHide(false)}
      onContinue={() => {
        if (!saving) form.validateAndSend();
      }}
      heading={folder ? tr('EditCompanyFolderSidebar.updateFolder', 'Upravit složku') : tr('EditCompanyFolderSidebar.createFolder', 'Vytvořit složku')}
    >
      {form.values && (
        <ColWithGap>
          <VerticalFieldAndValue
            label={tr('EditCompanyFolderSidebar.folderName', 'Název složky')}
            labelMode={'normal'}
            required={true}
            errorMessage={findValidationMessage(form.validationErrors, form.names.name, tr)}
          >
            <InputText value={form.values.name} onChange={e => form.setFieldValue(form.names.name, e.target.value)} />
          </VerticalFieldAndValue>

          {!folder && (
            <VerticalFieldAndValue label={tr('EditCompanyFolderSidebar.entityType', 'Typ')} labelMode={'normal'} required={true}>
              <InputEntityType value={form.values.entityType} onChange={e => form.setFieldValue(form.names.entityType, e)} options={entityTypes} />
            </VerticalFieldAndValue>
          )}

          <VerticalFieldAndValue
            label={tr('EditCompanyFolderSidebar.externalId', 'Externí ID (pro API)')}
            labelMode={'normal'}
            errorMessage={findValidationMessage(form.validationErrors, form.names.externalId, tr)}
          >
            <InputText value={form.values.externalId ?? ''} onChange={e => form.setFieldValue(form.names.externalId, e.target.value)} />
          </VerticalFieldAndValue>

          {/* RD-2977 phase 2: visibility targets apply to expedition, reservation and supplier-notification folders alike */}
          <RingilInputSwitch
            value={form.values.restricted}
            onChange={v => form.setFieldValue(form.names.restricted, v)}
            label={tr('EditCompanyFolderSidebar.restrictVisibility', 'Viditelná jen pro vybraná oddělení a uživatele')}
          />
          {form.values.restricted && (
            <VerticalFieldAndValue
              label={tr('EditCompanyFolderSidebar.visibleTo', 'Viditelná pro')}
              labelMode={'normal'}
              required={true}
              errorMessage={findValidationMessage(form.validationErrors, form.names.targets, tr)}
            >
              <NotifyDropdown
                notifyTargetOptions={targetOptions}
                values={selectedTargets}
                onSelect={values => form.setFieldValue(form.names.targets, values)}
                dataTestIdBase={'folder-visibility-targets'}
              />
            </VerticalFieldAndValue>
          )}

          {error && <COInlineMessage variant={'error'}>{error}</COInlineMessage>}
        </ColWithGap>
      )}
    </RingilSidebar>
  );
};
